import { ref } from 'vue';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  type RemoteConfig,
  getRemoteConfig,
  getAll,
  fetchAndActivate,
  ensureInitialized,
  getValue,
} from 'firebase/remote-config';
import { defineStore } from 'pinia';
import { isNil } from 'lodash-es';

type CampaignPromocode =
  | {
      code: string;
      isDomestic: boolean;
      airlines: string[] | null;
    }
  | '';

export type SerializedRemoteConfig = Record<
  string,
  boolean | string | CampaignPromocode | object
>;

/**
 * Values Firebase falls back to for every parameter the remote project does not
 * define, and the whitelist `serializeRemoteConfig` filters the response by.
 *
 * Object-shaped parameters are declared as **JSON strings**, not object
 * literals: Firebase only accepts `string | number | boolean` here and turns
 * anything else into `String(value)`, so `{}` used to reach consumers as the
 * useless `'[object Object]'`. `serializeRemoteConfig` JSON-parses every value
 * back, so `'{}'` arrives as `{}` — which is what consumers expect to index
 * into.
 */
const defaultConfig = {
  app_version: '',
  rwfree_zhuma_promo: '',
  rwfree_jasyl_juma_promo: '',
  trfree_show_cashback_hold: false,
  trfree_show_combo_pay_badge: true,
  trfree_show_cabinet_support_block: false,
  trfree_should_reset_dates: true,
  trfree_countdown_banner_config: '{}',
  avfree_campaign_promocode: '',
  avfree_features: '',
  avfree_war_zone_banner: '{}',
  avfree_quiz_config: {},
  cabfree_call_center_unavailable: '{}',
  cabfree_meta_migration: false,
  cabfree_show_esim_cross_sell: false,
  cabfree_show_insurance_cross_sell: false,
  cabfree_show_ticketon_main_button: false,
  cabfree_show_send_email: false,
  cabfree_swipe_to_refresh_enabled: false,
  avfree_middle_east_war_zone_modal: '{}',
  avfree_details_warning_banner: '{}',
  avfree_details_sale_banner: false,
  free_digital_nauryz_banner_enabled: false,
  avfree_promocode_modal: '{}',
  avfree_serp_loader_asset: '{}',
  avfree_pax_data_auto_filling_by_photo_enabled: false,
  avfree_main_page_layout: '',
  // Баннер отелей на выдаче уже живёт в проде включённым, поэтому дефолт —
  // `enabled: true`: пока Firebase недоступен, поведение остаётся прежним, а
  // выключение делается явным `false` из консоли.
  avfree_serp_hotel_banner: '{"enabled":true}',
  // Переменные формулы факторинга для бейджа на офферах: сумма оплаты равна
  // `заказ / (1 - discount)`, разбитая на `term` месяцев. Дефолт повторяет
  // текущие условия банка — 12 месяцев под 12% — чтобы бейдж считался так же,
  // пока Firebase не ответил.
  avfree_factoring_formula: '{"term":12,"discount":0.12}',
} as const;

/**
 * The webview regularly boots on a flaky mobile connection, where a single
 * failed `fetchAndActivate` used to cost the whole session its remote config.
 * Retry a couple of times with a linear backoff before giving up.
 *
 * The per-attempt timeout has to come down with it: the SDK defaults to a full
 * minute, so three attempts would leave `isLoading` (and everything waiting on
 * it) hanging for ~3 minutes. 10s x 3 attempts + backoff settles in ~33s —
 * faster than the single 60s attempt this replaces.
 */
const FETCH_RETRY_COUNT = 2;
const FETCH_RETRY_DELAY_MS = 1000;
const FETCH_TIMEOUT_MS = 10000;

const delay = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const fetchAndActivateWithRetry = (
  remoteConfig: RemoteConfig,
  attempt = 0,
): Promise<boolean> =>
  fetchAndActivate(remoteConfig).catch((error: unknown) => {
    if (attempt >= FETCH_RETRY_COUNT) {
      throw error;
    }

    return delay(FETCH_RETRY_DELAY_MS * (attempt + 1)).then(() =>
      fetchAndActivateWithRetry(remoteConfig, attempt + 1),
    );
  });

const parseJson = (value: string) => {
  try {
    const parsed = JSON.parse(value);
    return { isValid: true, value: parsed };
  } catch {
    return { isValid: false, value };
  }
};

const serializeRemoteConfig = (
  remoteConfig: RemoteConfig,
): SerializedRemoteConfig => {
  const defaultConfigKeys = Object.keys(defaultConfig);
  const remoteParameters = getAll(remoteConfig);

  return Object.keys(remoteParameters).reduce((acc, key) => {
    if (defaultConfigKeys.includes(key)) {
      const rawValue = remoteParameters[key].asString();
      const { isValid, value } = parseJson(rawValue);

      acc[key] = isValid ? value : rawValue;
    }
    return acc;
  }, {} as SerializedRemoteConfig);
};

export const useFirebaseStore = defineStore('firebase', () => {
  const serializedRemoteConfig = ref<SerializedRemoteConfig | null>(null);
  const isLoading = ref(false);

  let app: FirebaseApp;
  let rc: RemoteConfig;

  const init = (firebaseConfig: Record<string, string>) => {
    app = initializeApp(firebaseConfig);

    rc = getRemoteConfig(app);
    rc.defaultConfig =
      defaultConfig as unknown as RemoteConfig['defaultConfig'];
    rc.settings.minimumFetchIntervalMillis = 120000;
    rc.settings.fetchTimeoutMillis = FETCH_TIMEOUT_MS;

    isLoading.value = true;
    return fetchAndActivateWithRetry(rc)
      .then(() => {
        serializedRemoteConfig.value = serializeRemoteConfig(rc);
      })
      .catch(async error => {
        // The fetch is gone for good. Publish whatever Firebase already holds
        // locally — the config activated in an earlier session, otherwise
        // `defaultConfig` — so readers never see `null`. Leaving it `null` made
        // every `serializedRemoteConfig?.<key>` read resolve to `undefined`, so
        // each consumer silently degraded to its own hardcoded default (the
        // WhatsApp outage notice disappeared, remote-config-driven modals
        // never opened) and consumers that index into the config threw.
        await ensureInitialized(rc).catch(() => undefined);
        serializedRemoteConfig.value = serializeRemoteConfig(rc);

        // Rethrow so `withFirebase` still reports the failure to Sentry.
        throw error;
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const getBooleanValue = (key: keyof typeof defaultConfig) => {
    if (isNil(rc)) {
      return { value: Boolean(defaultConfig[key]), source: 'default' as const };
    }

    const remoteValue = getValue(rc, key);
    return { value: remoteValue.asBoolean(), source: remoteValue.getSource() };
  };

  return {
    serializedRemoteConfig,
    init,
    getBooleanValue,
    isLoading,
  };
});
